/*!
* Define Automatic Gain Controller properties and behaviour.
*/

async function getWorklet(ctx, options) {
	const path = window.location.pathname.split('/');
	const workletDir = path.slice(0, path.length - 1).join('/');
	await ctx.audioWorklet.addModule(`${workletDir}/agcWorklet.js`);
	return new AudioWorkletNode(ctx, 'agc-processor', options);
}

export default class AutoGainControl {
	/* DEFAULT LOGIC (METHODS) THAT SHOULD BE IMPLEMENTED IN EACH PLUGIN */

	// Used for getting current values for save
	getValues() {
		return {
			inputGain: this.inputGain,
			targetGain: this.targetGain,
			adjustmentRate: this.adjustmentRate,
			threshold: this.threshold,
			analyzeWindowSize: this.analyzeWindowSize,
			maxAttenuation: this.maxAttenuation,
			maxAmplification: this.maxAmplification
		}
	}

	// Used for setting stored values.
	// For each value, we check to see if it was passed in the 
	// values parameter before setting it to a default value
	// We'll do the same thing with a ternary for all the properties.
	setValues(values) {
		if (typeof values == 'undefined' || Object.keys(values).length === 0) { // if null or empty object passed
			this.updateInputGain(this.inputGain);
			this.updateTargetGain(this.targetGain);
			this.updateAdjustmentRate(this.adjustmentRate);
			this.updateThreshold(this.threshold);
			this.updateAnalyzeWindowSize(this.analyzeWindowSize);
			this.updateMaxAttenuation(this.maxAttenuation);
			this.updateMaxAmplification(this.maxAmplification);
		} else {
			this.updateInputGain(values.inputGain);
			this.updateTargetGain(values.targetGain);
			this.updateThreshold(values.threshold);
			this.updateAdjustmentRate(values.adjustmentRate);
			this.updateAnalyzeWindowSize(values.analyzeWindowSize);
			this.updateMaxAttenuation(values.maxAttenuation);
			this.updateMaxAmplification(values.maxAmplification);
		}
	}

	//Rows: array of rows (visually each row will be stacked vertically), inside array of elements - they will be stacked horizontally in a row - affectors (value changers) or indicators
	//Availale kinds: 'indicator', 'affector'
	//Available types for affectors: 'round-button' (min, max, step), 'slider' (min, max, step),
	//Available types for indicators: 'vu-meter' (min, max, orientation(vertical, horizontal))
	//Indicator can show values in pure value (like db) or percent, but better in pure value. Examples: { value: -15 }, { percent: 50 }
	getUI() {
		return {
			rows: [
				[
					{
						rows: [
							[{
								kind: 'affector', type: 'round-button', width: 75, title: 'Input Gain', round: 1, min: -6, max: 6, step: .1, unit: 'dB', setValue: (v) => this.updateInputGain(v), getValue: () => this.inputGain
							},
							{
								kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Threshold', min: -70, max: -20, step: 1, unit: 'dB', setValue: (v) => this.updateThreshold(v), getValue: () => this.threshold
							},
							{
								kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Adj. Rate', round: 1, min: 1, max: 60, step: .1, unit: 'dB/s', setValue: (v) => this.updateAdjustmentRate(v), getValue: () => this.adjustmentRate
							},
							],
							[
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#a770f9', title: 'Analyze Window Size', min: 100, max: 5000, step: 100, unit: 'ms', setValue: (v) => this.updateAnalyzeWindowSize(v), getValue: () => this.analyzeWindowSize
								},
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Max Attenuation', min: 0, max: 60, step: 1, unit: 'dB', setValue: (v) => this.updateMaxAttenuation(v), getValue: () => this.maxAttenuation
								},
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Max Amplification', min: 0, max: 60, step: 1, unit: 'dB', setValue: (v) => this.updateMaxAmplification(v), getValue: () => this.maxAmplification
								},
							]
						],
					},
					{
						kind: 'affector', type: 'round-button', width: 100, title: 'Target Gain', round: 1, min: -60, max: 0, step: .1, unit: 'dB', setValue: (v) => this.updateTargetGain(v), getValue: () => this.targetGain
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Attenuation', size: '180px', min: 0, max: 60, redPoint: 0, yellowPoint: 0, getValue: () => {
							return {
								// this.attenuationMetering / 2 to match max attenuation of the plugin
								value: this.attenuationMetering / 2
							}
						}
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Amplification', size: '180px', min: 0, max: 60, redPoint: 0, yellowPoint: 0, getValue: () => {
							return {
								// this.amplificationMetering / 2 to match max amplification of the plugin
								value: this.amplificationMetering / 2
							}
						}
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'In', min: -60, max: 3, size: '180px', getValue: () => {
							return {
								value: this.preMetering
							}
						}
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Out', min: -60, max: 3, size: '180px', getValue: () => {
							return {
								value: this.postMetering
							}
						}
					}
				]
			],
			width: 840,
			height: 340,
			color: '#900'
		}
	}


	/**
	 * Create nodes and connect them
	 * 
	 * @returns object containing input and output node
	 */
	async getAudioPipeline(opts) {
		this.ctx = opts?.audioContext || new AudioContext();

		// Create pre analyser node
		this.preAnalyserNode = this.ctx.createAnalyser(); // The input audio analyser

		// Input gain node
		this.inputGainNode = this.ctx.createGain();

		// Audio Worklet node to manipulate the gain conditionally
		this.agcProcessorNode = await getWorklet(this.ctx, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] });

		// Output gain node
		this.outputGainNode = this.ctx.createGain();

		// Create post analyser node
		this.postAnalyserNode = this.ctx.createAnalyser(); // The output audio analyser

		// Connect the nodes
		this.preAnalyserNode.connect(this.inputGainNode);
		this.inputGainNode.connect(this.agcProcessorNode);
		this.agcProcessorNode.connect(this.outputGainNode);
		this.outputGainNode.connect(this.postAnalyserNode);

		this.inputNode = this.preAnalyserNode;
		this.outputNode = this.postAnalyserNode;

		this.init();

		// To input will be connected source (with other effects), output will be connected to destination (or next effect)
		return {
			input: this.preAnalyserNode,
			output: this.postAnalyserNode
		}
	}

	// Called on playing state change (1 - playing, 0 - stop)
	setPlayState(value) {
		this.initialized = !!value;
		this.playing = !!value;

		if (!this.playing) {
			this.agcProcessorNode.port.postMessage('pause');
		} else {
			this.agcProcessorNode.port.postMessage('play');
			this.fadeIn();
		}

		this.log('Set play state changed', { value });

		if (value)
			this.updateMeters();
	}

	constructor(opts) {
		this.ctx = opts.context;
		this.player = opts.player;

		// Show console messages
		this.isDebug = opts.isDebug;

		this.log('Creating AGC... ');

		this.connected = false;

		// Default gain
		this.inputGain = 0;
		this.outputGain = 1;

		this.targetGain = -20; // Default target gain
		this.adjustmentRate = 5; // Default adjustment rate
		this.threshold = -70; // Default threshold
		this.analyzeWindowSize = 5000; // Default hold
		this.maxAttenuation = 60; // Default attenuation
		this.maxAmplification = 60; // Default max gain

		// Analyser properties
		this.fttSize = 2048; // bytes to allocate for data
		this.preMetering = null;
		this.postMetering = null;
		this.attenuationMetering = null;
		this.amplificationMetering = null;
		// this.latestAttenuationMeter = null;

		this.initialized = false;
	}

	/* PLUGIN LOGIC */

	// This is the last second logic that needs to execute for the plugin to work
	init() {
		this.initNodes(); // Use the values on compressor object to set values of the nodes

		this.initPreAnalyser();
		this.initPostAnalyser();

		this.initialized = true;
	}

	// Initialize values in nodes
	initNodes() {
		// Initialize pre analyser node
		this.preAnalyserNode.fftSize = this.fttSize;
		this.preFloatTimeData = new Float32Array(
			this.preAnalyserNode.frequencyBinCount
		);
		this.preAnalyserNode.getFloatTimeDomainData(
			this.preFloatTimeData
		);

		// Initialize post analyser node
		this.postAnalyserNode.fftSize = this.fttSize;
		this.postFloatTimeData = new Float32Array(
			this.postAnalyserNode.frequencyBinCount
		);
		this.postAnalyserNode.getFloatTimeDomainData(
			this.postFloatTimeData
		);

		// Set debug of AGC worklet
		this.agcProcessorNode.port.postMessage({ 'debug': this.isDebug });
	}

	initPreAnalyser() {
		// Analyzer for metering
		this.preAnalyserNode.fftSize = this.fttSize;

		this.preFloatTimeData = new Float32Array(this.preAnalyserNode.frequencyBinCount);
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);
	}

	initPostAnalyser() {
		// Analyzer for metering
		this.postAnalyserNode.fftSize = this.fttSize;

		this.postFloatTimeData = new Float32Array(this.postAnalyserNode.frequencyBinCount);
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
	}

	updateInputGain(value) {
		this.inputGain = value;

		if (!this.initialized)
			return;

		// Update the node ratio value
		this.inputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateTargetGain(value) {
		this.targetGain = value;

		if (!this.initialized)
			return;

		// Update the AGC target gain value
		this.agcProcessorNode.port.postMessage({ 'targetGain': value });
	}

	updateThreshold(value) {
		this.threshold = value;

		if (!this.initialized)
			return;

		// Update the AGC threshold value
		this.agcProcessorNode.port.postMessage({ 'threshold': value });
	}

	updateAdjustmentRate(value) {
		this.adjustmentRate = value;

		if (!this.initialized)
			return;

		// Update the adjustment rate value
		this.agcProcessorNode.port.postMessage({ 'adjustmentRate': value });
	}

	updateAnalyzeWindowSize(value) {
		this.analyzeWindowSize = value;

		if (!this.initialized)
			return;

		// Update the analyzeWindowSize value
		this.agcProcessorNode.port.postMessage({ 'analyzeWindowSize': value });
	}

	updateMaxAttenuation(value) {
		this.maxAttenuation = value;

		if (!this.initialized)
			return;

		// Update the max attenuation value
		this.agcProcessorNode.port.postMessage({ 'maxAttenuation': value });
	}

	updateMaxAmplification(value) {
		this.maxAmplification = value;

		if (!this.initialized)
			return;

		// Update the max amplification value
		this.agcProcessorNode.port.postMessage({ 'maxAmplification': value });
	}

	updateOutputGain(value) {
		this.outputGain = value;

		if (!this.initialized)
			return;

		// Update the node ratio value
		this.outputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	// The meters are acting as peak meters
	updateMeters() {
		// Schedule the next update
		if (this.playing) {
			requestAnimationFrame(() => this.updateMeters()); // Recursive
		}

		this.updatePreMeters();
		this.updatePostMeters();
		this.updateAttenuationMeter();
		this.updateAmplificationMeter();
	}

	updatePreMeters() {
		// Get the new frequency data
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);

		let sampleBuffer = this.preFloatTimeData;

		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		this.preMetering = this.convertGainToDecibels(peakInstantaneousPower);
	}

	updatePostMeters() {
		// Get the new frequency data
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);

		let sampleBuffer = this.postFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		this.postMetering = this.convertGainToDecibels(peakInstantaneousPower);
	}

	updateAttenuationMeter() {
		// Get the reduction value in dB from the compressor node
		let totalReduction = 0;

		if (this.postMetering < this.preMetering) {
			totalReduction = Math.abs(this.preMetering - this.postMetering);
		}

		this.attenuationMetering = totalReduction +
			Math.random() / 100; // Add randomness to make sure the meter updates every time
	}

	updateAmplificationMeter() {
		// Get the amplification value in dB by subtracting output level from input level
		let totalAmplification = 0;

		if (this.postMetering > this.preMetering) {
			totalAmplification = Math.abs(this.postMetering - this.preMetering);
		}

		this.amplificationMetering = totalAmplification +
			Math.random() / 100; // Add randomness to make sure the meter updates every time
	}

	fadeIn() {
		// 100 ms fade-in keeps it from sounding too jarring and also prevents popping
		let time = this.ctx.currentTime;
		this.outputGainNode.gain.setValueAtTime(0, this.ctx.currentTime);
		this.outputGainNode.gain.linearRampToValueAtTime(1.0, (time + 0.2));
		this.outputGainNode.gain.setValueAtTime(1.0, (time + 0.2));
	}

	convertDecibelsToGain(db) {
		return Math.pow(10, (db / 20));
	}

	convertGainToDecibels(gain) {
		return 20 * Math.log10(gain);
	}

	log(message) {
		if (!this.debug)
			return;

		console.debug(message);
	}
}